import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import FormField from '../components/ui/FormField';
import { Globals } from '../globalfunctions.js';
import roleManager, { PERMISSIONS } from '../utils/roleManager';
import { apiClient } from '../utils/apiClient';
import './KnowledgeBase.css';
import { alertDialog } from '../utils/dialogs';

const EMPTY_ARTICLE = { title: '', category: '', body: '', published: false };

function KnowledgeBase() {
  const navigate = useNavigate();
  const canEdit = roleManager.hasPermission(PERMISSIONS.EDIT_TICKETS);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // null = closed, {} with no id = creating, {id,...} = editing
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      setArticles(await apiClient.get('/api/kb'));
      setError('');
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
      return;
    }
    fetchArticles();
  }, [navigate]);

  const openCreate = () => { setFormError(''); setEditing({ ...EMPTY_ARTICLE }); };
  const openEdit = (article) => {
    if (!canEdit) return;
    setFormError('');
    setEditing({ id: article.id, title: article.title, category: article.category || '', body: article.body, published: article.published });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const payload = { title: editing.title, category: editing.category, body: editing.body, published: editing.published };
      if (editing.id) await apiClient.put(`/api/kb/${editing.id}`, payload);
      else await apiClient.post('/api/kb', payload);
      setEditing(null);
      fetchArticles();
    } catch (err) {
      setFormError(err.message || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeletingBusy(true);
    try {
      await apiClient.delete(`/api/kb/${deleting.id}`);
      setDeleting(null);
      setEditing(null);
      fetchArticles();
    } catch (err) {
      alertDialog(err.message || 'Failed to delete article');
    } finally {
      setDeletingBusy(false);
    }
  };

  const term = search.trim().toLowerCase();
  const filtered = articles.filter((a) => !term
    || a.title.toLowerCase().includes(term)
    || (a.category || '').toLowerCase().includes(term)
    || a.body.toLowerCase().includes(term));

  const columns = [
    { key: 'title', label: 'Title', sortable: true, sortValue: (a) => a.title, render: (a) => <span className="kb-title">{a.title}</span> },
    { key: 'category', label: 'Category', sortable: true, sortValue: (a) => a.category || '', render: (a) => a.category || '—' },
    {
      key: 'published',
      label: 'Status',
      sortable: true,
      sortValue: (a) => (a.published ? 1 : 0),
      render: (a) => <span className={`kb-status ${a.published ? 'kb-status-published' : 'kb-status-draft'}`}>{a.published ? 'Published' : 'Draft'}</span>
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      sortValue: (a) => new Date(a.updatedAt).getTime(),
      render: (a) => new Date(a.updatedAt).toLocaleDateString('en-GB')
    }
  ];

  return (
    <PageShell>
      <div className="kb-page">
        <div className="kb-header">
          <div>
            <h1>Knowledge Base</h1>
            <p className="kb-intro">Help articles customers can read in their portal. Drafts stay private until you publish them.</p>
          </div>
          {canEdit && <Button onClick={openCreate}><i className="fas fa-plus" aria-hidden="true"></i> New Article</Button>}
        </div>

        <input
          type="text"
          className="kb-search"
          aria-label="Search articles"
          placeholder="Search articles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? (
          <p aria-live="polite">Loading articles&hellip;</p>
        ) : error ? (
          <div className="kb-error">{error}</div>
        ) : (
          <ResponsiveTable
            columns={columns}
            rows={filtered}
            rowKey="id"
            onRowClick={canEdit ? openEdit : undefined}
            emptyMessage={search ? 'No articles match your search.' : 'No articles yet - write the first one.'}
          />
        )}
      </div>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit Article' : 'New Article'}
        size="large"
        footer={editing && (
          <>
            {editing.id && <Button variant="danger" onClick={() => setDeleting(editing)} disabled={saving}>Delete</Button>}
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button type="submit" form="kb-form" disabled={saving || !editing.title.trim() || !editing.body.trim()}>{saving ? 'Saving…' : 'Save'}</Button>
          </>
        )}
      >
        {editing && (
          <form id="kb-form" onSubmit={save}>
            {formError && <p className="kb-error" role="alert">{formError}</p>}
            <FormField id="kb-title" label="Title" required>
              <input id="kb-title" type="text" maxLength={200} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </FormField>
            <FormField id="kb-category" label="Category">
              <input id="kb-category" type="text" maxLength={80} placeholder="e.g. Email, Hosting, Billing" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
            </FormField>
            <FormField id="kb-body" label="Article" required>
              <textarea id="kb-body" rows={12} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
            </FormField>
            <label className="kb-publish-toggle">
              <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
              Published (visible to customers)
            </label>
          </form>
        )}
      </Modal>

      <Modal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Delete Article"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)} disabled={deletingBusy}>Cancel</Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deletingBusy}>{deletingBusy ? 'Deleting…' : 'Delete'}</Button>
          </>
        )}
      >
        <p>Permanently delete <strong>{deleting?.title}</strong>? This cannot be undone.</p>
      </Modal>
    </PageShell>
  );
}

export default KnowledgeBase;
